import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import {
  BuildingResponseWithClassesAttributes,
  ClassResponse,
} from '../../../../../api/types/settings/buildings/building';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import './settings-building-classes.css';
import { addNewClassListAttributesValuesOperations } from './operations/add-new-class-list-attributes-values-operations';
import { AddClassAttributesValuesDialog } from './components/add-class-attributes-values-dialog';
import { Dialog } from 'primereact/dialog';
import { AddNewClassAttributesDialog } from '../../attributes/dialogs/add-new-class-attributes-dialog';
import { AddNewClassDialog } from '../../branches/buildings/classes/componenets/add-new-class-dialog';

export interface SettingsBuildingClassesDatamodel {
  buildingId: number;
}

export const SettingsBuildingClasses: React.FC<SettingsBuildingClassesDatamodel> =
  observer(({ buildingId }) => {
    const [building, setBuilding] =
      useState<BuildingResponseWithClassesAttributes | null>(null);
    const [loading, setLoading] = useState(false);
    const [attributeValues, setAttributeValues] = useState<Record<string, any>>(
      {},
    );
    const [changedValues, setChangedValues] = useState<
      Record<
        number, // classId
        {
          listId: number;
          attributeId: number;
          numericValue?: number;
          boolValue?: boolean;
        }[]
      >
    >({});
    const [showAddAttributesDialog, setShowAddAttributesDialog] =
      useState(false);
    const [selectedClass, setSelectedClass] = useState<ClassResponse | null>(
      null,
    );

    const [addClassActive, setAddClassActive] = useState(false);

    const asd = () => {
      const currentBuilding = globalSettingsBranches.buildings.find(
        (b) => b.id === Number(buildingId),
      );
      if (currentBuilding) {
        setBuilding(currentBuilding);

        // Initialize attribute values from the building data
        const initialValues: Record<string, any> = {};
        currentBuilding.classes.forEach((classData) => {
          classData.attributes.forEach((attributeList) => {
            attributeList.attributes.forEach((attribute) => {
              const key = `${classData.id}-${attribute.attributeId}`;
              if (attribute.attributeType === 'numeric') {
                initialValues[key] = attribute.numericValue;
              } else if (attribute.attributeType === 'bool') {
                initialValues[key] = attribute.boolValue;
              }
            });
          });
        });
        setAttributeValues(initialValues);
      }
    };

    useEffect(() => asd(), [buildingId, globalSettingsBranches.buildings]);

    const handleAttributeValueChange = async (
      classId: number,
      attributeId: number,
      numericValue?: number | null,
      booleanValue?: boolean | null,
    ) => {
      // Update local state immediately for UI responsiveness
      const key = `${classId}-${attributeId}`;
      setAttributeValues((prev) => ({
        ...prev,
        [key]: numericValue !== null ? numericValue : booleanValue,
      }));

      // Find the listId for this attribute
      const classData = building?.classes.find((cls) => cls.id === classId);
      if (!classData) return;

      let targetListId: number | null = null;
      for (const attributeList of classData.attributes) {
        const attribute = attributeList.attributes.find(
          (attr) => attr.attributeId === attributeId,
        );
        if (attribute) {
          targetListId = attributeList.listId;
          break;
        }
      }

      if (targetListId === null) return;

      // Track the change for this class
      setChangedValues((prev) => {
        const existing = prev[classId] || [];
        const existingIndex = existing.findIndex(
          (item) => item.attributeId === attributeId,
        );

        const newChange = {
          listId: targetListId!,
          attributeId,
          numericValue: numericValue ?? undefined,
          boolValue: booleanValue ?? undefined,
        };

        if (existingIndex >= 0) {
          // Update existing change
          const updated = [...existing];
          updated[existingIndex] = newChange;
          return { ...prev, [classId]: updated };
        } else {
          // Add new change
          return { ...prev, [classId]: [...existing, newChange] };
        }
      });
    };

    const handleSaveClassChanges = async (classId: number) => {
      const classChanges = changedValues[classId];
      if (!classChanges || classChanges.length === 0) return;

      try {
        setLoading(true);

        // Prepare all attribute changes for the class
        const attributeValues = classChanges.map((change) => ({
          attributeId: change.attributeId,
          numericValue: change.numericValue,
          boolValue: change.boolValue,
        }));

        // Use the first listId (assuming all attributes belong to the same logical operation)
        const listId = classChanges[0].listId;

        // Make a single API call with all attributes for this class
        await addNewClassListAttributesValuesOperations(
          buildingId,
          classId,
          listId,
          attributeValues,
          setLoading,
          () => {
            //
          },
          () => {
            //
          },
        );

        // Clear changes for this class
        setChangedValues((prev) => {
          const updated = { ...prev };
          delete updated[classId];
          return updated;
        });

        // Refresh the building data
        const currentBuilding = globalSettingsBranches.buildings.find(
          (b) => b.id === Number(buildingId),
        );
        if (currentBuilding) {
          setBuilding(currentBuilding);

          // Reinitialize attribute values
          const initialValues: Record<string, any> = {};
          currentBuilding.classes.forEach((classData) => {
            classData.attributes.forEach((attributeList) => {
              attributeList.attributes.forEach((attribute) => {
                const key = `${classData.id}-${attribute.attributeId}`;
                if (attribute.attributeType === 'numeric') {
                  initialValues[key] = attribute.numericValue;
                } else if (attribute.attributeType === 'bool') {
                  initialValues[key] = attribute.boolValue;
                }
              });
            });
          });
          setAttributeValues(initialValues);
        }

        toast.success('השינויים נשמרו בהצלחה');
      } catch (error) {
        toast.error('שגיאה בשמירת השינויים');
      } finally {
        setLoading(false);
      }
    };

    const hasClassChanges = (classId: number) => {
      return changedValues[classId] && changedValues[classId].length > 0;
    };

    const handleAddAttributesClick = (classData: ClassResponse) => {
      setSelectedClass(classData);
      setShowAddAttributesDialog(true);
    };

    const handleAddAttributesSuccess = () => {
      // Refresh the building data after successful addition
      const currentBuilding = globalSettingsBranches.buildings.find(
        (b) => b.id === Number(buildingId),
      );
      if (currentBuilding) {
        setBuilding(currentBuilding);

        // Reinitialize attribute values
        const initialValues: Record<string, any> = {};
        currentBuilding.classes.forEach((classData) => {
          classData.attributes.forEach((attributeList) => {
            attributeList.attributes.forEach((attribute) => {
              const key = `${classData.id}-${attribute.attributeId}`;
              if (attribute.attributeType === 'numeric') {
                initialValues[key] = attribute.numericValue;
              } else if (attribute.attributeType === 'bool') {
                initialValues[key] = attribute.boolValue;
              }
            });
          });
        });
        setAttributeValues(initialValues);
      }
    };

    const renderAttributeValue = (attribute: any, classId: number) => {
      const key = `${classId}-${attribute.attributeId}`;
      const value = attributeValues[key];

      switch (attribute.attributeType) {
        case 'numeric':
          return (
            <InputNumber
              value={value}
              onChange={(e) =>
                handleAttributeValueChange(
                  classId,
                  attribute.attributeId,
                  e.value,
                  null,
                )
              }
              className="w-full"
            />
          );
        case 'bool':
          return (
            <InputSwitch
              checked={value}
              onChange={(e) =>
                handleAttributeValueChange(
                  classId,
                  attribute.attributeId,
                  null,
                  e.value,
                )
              }
            />
          );
        default:
          return <span>Unsupported type {attribute.attributeType}</span>;
      }
    };

    const renderClassAttributes = (classData: ClassResponse) => {
      return (
        <div className="p-4">
          {classData.attributes.map((attributeList, index) => (
            <div
              key={attributeList.listId}
              className="attribute-list-container"
            >
              <div className="attribute-list-header">
                <h3 className="text-xl mb-3">{attributeList.listName}</h3>
                <Tag
                  value={`${attributeList.attributes.length} attributes`}
                  severity="info"
                  className="attribute-count-tag"
                />
              </div>
              <div className="attribute-grid">
                {attributeList.attributes.map((attribute) => (
                  <div key={attribute.attributeId} className="attribute-item">
                    <div className="attribute-content">
                      <div className="attribute-info">
                        <span className="attribute-name">
                          {attribute.attributeName}
                        </span>
                        <Tag
                          value={attribute.attributeType}
                          severity="secondary"
                          className="attribute-type-tag"
                        />
                      </div>
                      <div className="attribute-value">
                        {renderAttributeValue(attribute, classData.id)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {index < classData.attributes.length - 1 && (
                <div className="attribute-list-divider" />
              )}
            </div>
          ))}
        </div>
      );
    };

    return (
      <div>
        <div>
          <div className="card mt-4">
            <div className="header-container">
              <h2 className="page-title">{building?.buildingName} - מבנה</h2>
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
                className="attributes-count"
              >
                <Tag
                  value={`${building?.classes?.reduce((total, cls) => total + cls.attributes.reduce((acc, list) => acc + list.attributes.length, 0), 0) || 0} סה״כ תכונות`}
                  severity="info"
                  className="total-attributes-tag"
                />
                <Button
                  label={'הוספת כיתה'}
                  icon="pi pi-plus"
                  onClick={() => setAddClassActive(true)}
                />
              </div>
            </div>

            <Accordion className="class-accordion">
              {building?.classes?.map((classData) => (
                <AccordionTab
                  key={classData.id}
                  header={
                    <div className="class-header">
                      <span className="class-name">{classData.className}</span>
                      <div className="class-header-actions">
                        <Tag
                          value={`${classData.attributes.reduce((acc, list) => acc + list.attributes.length, 0)} תכונות`}
                          severity="info"
                          className="class-attributes-tag"
                        />
                      </div>
                    </div>
                  }
                >
                  <div className="class-content">
                    <div className="class-actions mb-3">
                      <Button
                        label="הוספת תכונות"
                        icon="pi pi-plus"
                        severity="info"
                        size="small"
                        onClick={() => handleAddAttributesClick(classData)}
                      />
                    </div>
                    {renderClassAttributes(classData)}

                    {hasClassChanges(classData.id) && (
                      <Button
                        label="אישור"
                        icon="pi pi-check"
                        severity="success"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent accordion toggle
                          handleSaveClassChanges(classData.id);
                        }}
                        loading={loading}
                        className="mr-2"
                      />
                    )}
                  </div>
                </AccordionTab>
              ))}
            </Accordion>
          </div>
        </div>
        <AddClassAttributesValuesDialog
          visible={showAddAttributesDialog}
          onHide={() => setShowAddAttributesDialog(false)}
          buildingId={buildingId}
          classId={selectedClass?.id || 0}
          className={selectedClass?.className || ''}
          onSuccess={handleAddAttributesSuccess}
        />

        <Dialog
          header="הוספת רשימת מאפיינים"
          visible={addClassActive}
          onHide={() => setAddClassActive(false)}
          style={{
            width: '600px',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
          modal
          dismissableMask
        >
          <AddNewClassDialog
            branchId={1}
            buildingId={buildingId}
            closeDialog={() => setAddClassActive(false)}
          />
        </Dialog>
      </div>
    );
  });
